import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const FAQS = [
  {
    question: "How is Shadow different from an AI note-taker?",
    answer:
      "AI note-takers transcribe and summarize meetings after they end. Shadow works before, during, and after every call — preparing rep briefs from CRM and account history, surfacing real-time intelligence during the conversation, and completing action items like CRM updates, email drafts, and Jira tickets automatically.",
  },
  {
    question: "What CRM systems does Shadow connect to?",
    answer:
      "Shadow integrates with Salesforce today, with HubSpot support coming soon. Shadow reads account history and opportunity context from your CRM before calls, and writes meeting summaries, stage changes, and next steps back to the record after the call.",
  },
  {
    question: "Which tools does Shadow push completed work to?",
    answer:
      "After a call, Shadow can update Salesforce, draft Gmail or Outlook follow-ups, create tickets in Jira or Linear, post summaries to Slack, and file notes in Notion — based on your team's existing workflow.",
  },
  {
    question: "Does Shadow store or train on my meeting data?",
    answer:
      "No. Your meeting data is never used to train any models. Recordings and transcripts stay yours and are encrypted in transit and at rest. You control what is stored, shared, and deleted.",
  },
  {
    question: "What meeting platforms does Shadow support?",
    answer:
      "Shadow works with Google Meet, Zoom, and Microsoft Teams today. The macOS desktop app joins meetings automatically and does not require a bot in the call.",
  },
  {
    question: "Is Shadow right for enterprise revenue teams?",
    answer:
      "Yes. Shadow is built for AEs, account managers, and revenue leaders who run high-volume, high-stakes conversations. Enterprise plans include SSO, team-wide deployment, custom integrations, and dedicated support.",
  },
];

function DarkFaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/[0.08] last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 py-5 text-left"
      >
        <span className="text-[15px] font-medium text-white/85 sm:text-base">{question}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-white/40 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-[14px] leading-relaxed text-white/50 sm:text-[15px]">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FaqsSection() {
  return (
    <section id="faqs" className="relative overflow-hidden bg-[#070D1A] py-20 sm:py-28">
      {/* Dot grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />
      {/* Subtle glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/4 top-1/2 h-[400px] w-[500px] -translate-y-1/2 rounded-full bg-[#0092B4]/[0.07] blur-[120px]" />
      </div>

      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">

          {/* Left — heading */}
          <div className="lg:col-span-4">
            <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-bold tracking-tight text-white">
              FAQs
            </h2>
            <p className="mt-3 text-[14px] text-slate-400">
              More questions? Email us at{" "}
              <a href="mailto:hello@shadowlabs.ai" className="text-[#0092B4] hover:underline">
                hello@shadowlabs.ai
              </a>
            </p>
          </div>

          {/* Right — accordion */}
          <div className="lg:col-span-8">
            {FAQS.map(faq => (
              <DarkFaqItem key={faq.question} {...faq} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
